const DEFAULT_IMAGE_OPTIMIZATION_OPTIONS = {
  format: 'webp',
  quality: '80',
};

export interface ImageOptimizationOptions {
  width?: number | string | null;
  height?: number | string | null;
  format?: string | null;
  quality?: number | string | null;
}

const ALLOWED_DOMAINS = ['image-farm', 'product-image'];

/**
 * URL에서 파일 확장자를 안전하게 추출합니다.
 */
const getFileExtension = (url: string): string | undefined => {
  try {
    const { pathname } = new URL(url);
    return pathname.split('.').pop()?.toLowerCase();
  } catch {
    return undefined;
  }
};

/**
 * 이미지 URL에 지정된 width/format/quality 파라미터를 강제로 적용한다.
 * 기존 동일 키가 있으면 덮어쓰고, 없는 경우에는 추가한다.
 * 원본 이미지가 png인 경우 transparent=true 파라미터를 자동으로 추가한다.
 */
export const withImageOptimizationQuery = (
  url?: string | null,
  options: ImageOptimizationOptions = {},
): string | null => {
  if (!url) {
    return null;
  }

  // 특정 CDN 도메인이 포함되지 않은 경우 원본 반환 (상대 경로 포함)
  if (!ALLOWED_DOMAINS.some((domain) => url.includes(domain))) {
    return url;
  }

  const extension = getFileExtension(url);

  // 원본이 이미 webp인 경우 아무런 처리 없이 원본 반환
  if (extension === 'webp') {
    return url;
  }

  const { width, height, format, quality } = options;
  const newParams = new Map<string, string>();

  // 기본값 설정
  newParams.set('format', DEFAULT_IMAGE_OPTIMIZATION_OPTIONS.format);
  newParams.set('quality', DEFAULT_IMAGE_OPTIMIZATION_OPTIONS.quality);

  if (width !== undefined && width !== null) newParams.set('width', String(width));
  if (height !== undefined && height !== null) newParams.set('height', String(height));
  if (format !== undefined && format !== null) newParams.set('format', String(format));
  if (quality !== undefined && quality !== null) newParams.set('quality', String(quality));

  // PNG 파일인 경우 transparent=true 자동 추가
  if (extension === 'png') {
    newParams.set('transparent', 'true');
  }

  try {
    const urlObj = new URL(url);
    newParams.forEach((value, key) => {
      urlObj.searchParams.set(key, value);
    });
    return urlObj.toString();
  } catch {
    // URL 파싱 실패 시 원본 반환 (도메인 체크를 통과했더라도 유효하지 않은 URL일 수 있음)
    return url;
  }
};
